import { z } from 'zod'
import {
  ExploitChainsPhaseSchema,
  ExploitChainsStatusSchema,
  FindingOverlayPhaseSchema,
  FindingOverlayStatusSchema,
  ThreatModelPhaseSchema,
  ThreatModelStatusSchema,
} from './api-schemas.ts'

/**
 * Zod schemas for Server-Sent Event (SSE) analysis stream events.
 *
 * These events stream real-time analysis progress for individual findings
 * during scan analysis via `GET /api/v1/scans/{scan_id}/events`.
 * Event format: `data: {json}\n\n`
 */

// Start event - signals workflow has begun
export const StartEventSchema = z
  .object({
    type: z.literal('start'),
    id: z.string(),
    timestamp: z.string(),
  })
  .describe('StartEvent')

// Finding progress content - the data inside data-progress-finding events
export const FindingProgressContentSchema = z
  .object({
    finding_id: z.string(),
    type: z.enum(['triage', 'fix', 'sca']),
    status: z.enum(['in_progress', 'completed', 'error']),
    title: z.string(),
    description: z.string(),
    completed_events: z.number().optional(),
    total_events: z.number().optional(),
  })
  .describe('FindingProgressContent')

// Data progress finding event - individual finding analysis updates
export const DataProgressFindingEventSchema = z
  .object({
    type: z.literal('data-progress-finding'),
    content: FindingProgressContentSchema,
  })
  .describe('DataProgressFindingEvent')

// Workflow event - overall workflow status updates
export const WorkflowEventSchema = z
  .object({
    type: z.literal('workflow'),
    content: z.object({
      status: z.enum(['in_progress', 'completed', 'error']),
      title: z.string(),
      description: z.string(),
      error: z.string().optional(),
    }),
  })
  .describe('WorkflowEvent')

// Triage completion content - signals triage process is complete for a finding
export const TriageCompleteContentSchema = z
  .object({
    finding_id: z.string(),
    type: z.literal('triage'),
    resultType: z.enum(['TRIAGED', 'RULE_NOT_SUPPORTED', 'ERROR']),
    triageStatus: z.enum(['TRUE_POSITIVE', 'FALSE_POSITIVE', 'WONT_FIX', 'SUSPICIOUS']).nullable(),
  })
  .describe('TriageCompleteContent')

// Fix completion content - signals fix process is complete for a finding
export const FixCompleteContentSchema = z
  .object({
    finding_id: z.string(),
    type: z.literal('fix'),
    outcome: z.enum(['fixed', 'blocked', 'excluded', 'out_of_scope']),
    reason: z.string().nullable().optional(),
  })
  .describe('FixCompleteContent')

// SCA completion content - signals SCA analysis is complete for a finding
export const ScaCompleteContentSchema = z
  .object({
    finding_id: z.string(),
    type: z.literal('sca'),
    outcome: z.enum(['completed', 'excluded']),
    reason: z.string().nullable().optional(),
  })
  .describe('ScaCompleteContent')

// Discriminated union for finding completion events
export const FindingCompleteContentSchema = z
  .discriminatedUnion('type', [TriageCompleteContentSchema, FixCompleteContentSchema, ScaCompleteContentSchema])
  .describe('FindingCompleteContent')

// Data progress finding complete event - signals triage or fix process is complete
export const DataProgressFindingCompleteEventSchema = z
  .object({
    type: z.literal('data-progress-finding-complete'),
    content: FindingCompleteContentSchema,
  })
  .describe('DataProgressFindingCompleteEvent')

// Finish event - signals workflow has completed
export const FinishEventSchema = z
  .object({
    type: z.literal('finish'),
    id: z.string(),
    timestamp: z.string(),
    usage: z.record(z.string(), z.unknown()).optional(),
  })
  .describe('FinishEvent')

// Error event - signals an error occurred
export const ErrorEventSchema = z
  .object({
    type: z.literal('error'),
    content: z.string(),
  })
  .describe('ErrorEvent')

// Discriminated union of all SSE event types
export const AnalysisStreamEventSchema = z
  .discriminatedUnion('type', [
    StartEventSchema,
    DataProgressFindingEventSchema,
    DataProgressFindingCompleteEventSchema,
    WorkflowEventSchema,
    FinishEventSchema,
    ErrorEventSchema,
  ])
  .describe('AnalysisStreamEvent')

// Support bundle phase event - emitted on every phase transition by the platform (ISS-7005)
export const SupportBundlePhaseEventSchema = z
  .object({
    id: z.string(),
    phase: z.enum(['pending', 'running', 'succeeded', 'failed']),
    elapsed_seconds: z.number().int().nonnegative(),
  })
  .describe('SupportBundlePhaseEvent')

// Threat model progress event - emitted on every phase transition while a repository's
// threat model is generating (ENH-539). On the wire, the SSE `event:` name is the phase
// for progress events (status `processing`) or `completed`/`failed` for the terminal
// event; the parser drives off the `data:` payload, which carries the same information.
// A terminal `failed` event additionally carries `message` = the failure reason.
// Progress event for a repository-scoped generation stream (threat model, exploit chains). The SSE
// `event:` name is the phase for progress events (status `processing`) or `completed`/`failed` for
// the terminal event; a terminal `failed` event additionally carries `message` = the failure reason.
const createProgressStreamEventSchema = <TPhase extends z.ZodType, TStatus extends z.ZodType>(
  phaseSchema: TPhase,
  statusSchema: TStatus,
  name: string
) =>
  z
    .object({
      phase: phaseSchema,
      status: statusSchema,
      timestamp: z.string(),
      message: z.string().optional(),
    })
    .describe(name)

export const ThreatModelStreamEventSchema = createProgressStreamEventSchema(
  ThreatModelPhaseSchema,
  ThreatModelStatusSchema,
  'ThreatModelStreamEvent'
)

export const ExploitChainsStreamEventSchema = createProgressStreamEventSchema(
  ExploitChainsPhaseSchema,
  ExploitChainsStatusSchema,
  'ExploitChainsStreamEvent'
)

export const FindingOverlaysStreamEventSchema = createProgressStreamEventSchema(
  FindingOverlayPhaseSchema,
  FindingOverlayStatusSchema,
  'FindingOverlaysStreamEvent'
)

// Type exports
export type StartEvent = z.infer<typeof StartEventSchema>
export type FindingProgressContent = z.infer<typeof FindingProgressContentSchema>
export type DataProgressFindingEvent = z.infer<typeof DataProgressFindingEventSchema>
export type TriageCompleteContent = z.infer<typeof TriageCompleteContentSchema>
export type FixCompleteContent = z.infer<typeof FixCompleteContentSchema>
export type ScaCompleteContent = z.infer<typeof ScaCompleteContentSchema>
export type FindingCompleteContent = z.infer<typeof FindingCompleteContentSchema>
export type DataProgressFindingCompleteEvent = z.infer<typeof DataProgressFindingCompleteEventSchema>
export type WorkflowEvent = z.infer<typeof WorkflowEventSchema>
export type FinishEvent = z.infer<typeof FinishEventSchema>
export type ErrorEvent = z.infer<typeof ErrorEventSchema>
export type AnalysisStreamEvent = z.infer<typeof AnalysisStreamEventSchema>
export type SupportBundlePhaseEvent = z.infer<typeof SupportBundlePhaseEventSchema>
export type ThreatModelStreamEvent = z.infer<typeof ThreatModelStreamEventSchema>
export type ExploitChainsStreamEvent = z.infer<typeof ExploitChainsStreamEventSchema>
export type FindingOverlaysStreamEvent = z.infer<typeof FindingOverlaysStreamEventSchema>

// Backwards compatibility alias - FindingProgressContent is what consumers need for the event buffer
export type FindingProgressEvent = FindingProgressContent
